import type { FC } from 'react';
import { useEffect } from 'react';

import { Box, Col, Text } from '@opendoor/bricks-next';
import { PropertyCard } from 'buying/components/propertyCards/PropertyCard';
import type { ShowingRequestPropertyQuery_marketplace_MarketplaceQuery_property_MarketplaceProperty as MarketplaceProperty } from 'buying/graphql/queries/__generated__/ShowingRequestProperty';
import { googleStreetViewUrl } from 'buying/helpers/images';
import { Button } from 'platform/bricks-next';
import { useToast } from 'shared/hooks/useToast';

export type ConfirmationProperty = MarketplaceProperty & {
  opendoorOwned?: boolean | null;
  visitationOptions?: Array<{ option: string }>;
  instantTours?: boolean;
  street?: string;
  __typename?: string;
  id: string;
  publishedAt: string;
  badges: string[];
  openHouses: string[];
  thumbnailLegalText: string;
  thumbnailLegalLogo: string;
};

interface IPropertyConfirmationProps {
  property?: ConfirmationProperty | null;
  handleOnNext: () => void;
  opendoorOwned?: boolean;
  isHomebuilder?: boolean;
}

const isPropertyUnlockable = (property: ConfirmationProperty) => {
  return (
    !!property?.instantTours ||
    !!property?.visitationOptions?.some((item) => item.option === 'all_day_open_house')
  );
};
/**
 * Property confirmation step
 *
 */
export const PropertyConfirmation: FC<IPropertyConfirmationProps> = ({
  property,
  handleOnNext,
}) => {
  const { showToast, ToastElement } = useToast({
    type: 'info',
    analyticsName: 'cake-tour-creation-flow-tour-unavailable',
    message: 'This property is not currently available to tour.',
  });
  const cannotServiceTour = property && !isPropertyUnlockable(property);
  const buttonText = cannotServiceTour ? 'Done' : 'Continue';

  useEffect(() => {
    if (cannotServiceTour) {
      showToast();
    }
  }, [cannotServiceTour, showToast]);

  return (
    <Col alignItems="center" mt="$24x" gap="$4x" flex={1}>
      <Box display="block" mb="$8x">
        <Text tag="h2" typography="$subheaderLarge" mb="$4x" $platform-web={{ fontWeight: 700 }}>
          Tour {property?.street}
        </Text>
      </Box>
      <Col width="100%" $largerThanSM={{ width: 350 }} gap="$4x">
        {property && (
          <PropertyCard
            address={property.street}
            beds={property.bedrooms}
            baths={property.bathroomsTotal}
            city={property.city}
            photoUrl={
              property?.photos && property?.photos?.length > 0
                ? `${property?.photos[0]}?preset=3%3A2-sm&service=buyer`
                : googleStreetViewUrl({
                    type: 'address',
                    street: property.street,
                    city: property.city,
                    state: property?.state || '',
                  })
            }
            price={property.currentListPrice}
            sqft={property.totalLivingSqFt}
          />
        )}
        <Button
          variant="primary"
          analyticsName="cake-tour-creation-flow-property-confirmation-next"
          aria-label={buttonText}
          onClick={handleOnNext}
          mt={5}
          label={buttonText}
          size="medium"
        />
      </Col>
      {ToastElement}
    </Col>
  );
};
